import { useEffect, useState } from 'react';
import IconButton from '../../../juristec-ui/core/IconButton';
import Tooltip from '../../../juristec-ui/core/Tooltip';
import Chart from '../../../juristec-ui/kpis/charts';
import InsightCard from '../../../juristec-ui/kpis/insight';
import Popover from '../../../juristec-ui/core/Popover';
import Avatar from '../../../juristec-ui/core/Avatar';

import KpiStatus, { checkKpiLimit } from '../../../juristec-ui/kpis/utils/chartTools/KpiStatus';
import { formatDateTime } from '../../../juristec-ui/utils/functions/lab';
import useToggleState from '../../../juristec-ui/hooks/useToggleState';
import getCardStyle from '../../../utils/functions/getCardStyle';
import assistantAvatar from '../../../assets/temp.png';

import { Close } from '../../../juristec-ui/icons';

import {
  KpiCard,
  KpiContainer,
  KpiTitle,
  KpiWrapper,
  MainContainer,
  Header,
  ResumeContainer,
} from './styled';

type GlobalConfig = Record<string, string | number | boolean>;

interface ResumeKpiViewProps {
  close: () => void;
  resume: string;
  timestamp: Date;
  kpi: any;
  cardStyles: Record<string, any>;
}

const ResumeKpiView = ({
  close,
  resume,
  timestamp,
  kpi,
  cardStyles,
}: ResumeKpiViewProps) => {
  const [showResume, setToggleResume, setCloseResume] = useToggleState(true);
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>({
    useGlobalTheme: false, useGlobalFont: false,
  });

  useEffect(() => {
    setGlobalConfig({
      ...(cardStyles ?? {}),
      useGlobalTheme: !!cardStyles
        && cardStyles.useGlobalTheme
        && kpi?.style?.UseGlobalColor !== false,
      useGlobalFont: !!cardStyles
        && cardStyles.useGlobalFont
        && kpi?.style?.UseGlobalFont !== false,
    });
  }, [cardStyles, kpi]);

  const getValueBackground = (isValue: boolean, background: string) => {
    if (isValue && globalConfig?.useGlobalTheme) {
      return { backgroundColor: String(globalConfig.valueBg) };
    } return { backgroundColor: isValue ? background : 'transparent' };
  };

  const titleStyle = (tStyle: Record<string, string>): Record<string, string> => ({
    fontSize: `${tStyle?.fontSize ?? 20}px`,
    color: globalConfig?.useGlobalTheme ? String(globalConfig.titles) : (tStyle?.color ?? '#000000'),
    fontFamily: `'${(globalConfig?.useGlobalFont ? globalConfig.fontTitle : tStyle?.fontFamily) ?? 'arial'}', Arial`,
    fontWeight: tStyle?.bold ? 'bold' : 'normal',
    textDecoration: tStyle?.underline ? 'underline' : 'none',
    fontStyle: tStyle?.italic ? 'italic' : 'normal',
    textAlign: tStyle?.align ?? 'center',
    width: '100%',
    padding: '0 2px',
  });

  return (
    <MainContainer>
      <Header>
        <Popover
          open={showResume}
          closePopover={setCloseResume}
          style={{
            zIndex: 1050,
            backgroundColor: 'white',
            color: 'black',
          }}
          offset={[10, 10]}
          atModal
        >
          <Popover.Action>
            <Tooltip text="Resumo" atModal direction="bottom-end">
              <Avatar
                size="medium"
                name="Assistente"
                src={assistantAvatar || undefined}
                onClick={setToggleResume}
              />
            </Tooltip>
          </Popover.Action>
          {/* @ts-expect-error not typed yet */}
          <Popover.Content>
            <ResumeContainer>
              <p>{resume}</p>
            </ResumeContainer>
          </Popover.Content>
        </Popover>
        <h1>{formatDateTime(timestamp, { time: 'half' })}</h1>
        <Tooltip text="Fechar" atModal direction="bottom-end">
          <IconButton
            size="large"
            color="white"
            style={{ boxShadow: 'none', padding: '5px' }}
            onClick={close}
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Header>
      <KpiContainer>
        <KpiCard
          // size={resizeKpi()}
          // @ts-expect-error not typed yet
          style={getCardStyle(cardStyles || {})}
          kpiType={kpi?.style?.type ?? kpi?.type}
          titleFontSize={
            kpi?.style?.ShowTitleControl?.checked
              ? kpi?.style?.StyleTitleControl?.fontSize : 0
          }
          showTitle={kpi?.style?.ShowTitleControl?.checked}
        >
          {['label', 'smartLabel', 'Content'].includes(kpi?.type) && (
            <InsightCard
              item={kpi}
              globalConfig={globalConfig}
            />
          )}
          {!['label', 'smartLabel', 'Iframe', 'Content'].includes(kpi?.type) && (
            <>
              {kpi?.style?.ShowTitleControl?.checked ? (
                <KpiTitle
                  style={getValueBackground(
                    (kpi?.style?.type ?? kpi?.type) === 'Value',
                    kpi?.style?.GlobalValue?.background,
                  )}
                >
                  <span style={titleStyle(kpi?.style?.StyleTitleControl)}>
                    {kpi?.name}
                  </span>
                </KpiTitle>
              ) : <></>}
              <KpiWrapper>
                <KpiStatus
                  conditions={([
                    [
                      kpi?.meta?.error,
                      kpi?.meta?.error?.length > 0,
                    ],
                    [
                      'EmptyDataSet',
                      !(kpi?.data?.data?.length > 0) && kpi?.type !== 'Value',
                    ],
                    [
                      'TooManyCategories',
                      checkKpiLimit(
                        kpi?.type,
                        kpi?.data?.columns?.length ?? 0,
                        kpi?.data?.index?.length ?? 0,
                      ),
                    ],
                  ])}
                  message={
                    kpi?.meta?.errorMessage?.length > 0
                      ? kpi.meta.errorMessage : undefined
                  }
                >
                  <>
                    <Chart
                      key={kpi?.id}
                      type={kpi?.type}
                      data={kpi?.data}
                      kpiSize={undefined}
                      handleDrilldownClick={null}
                      valueFunction={kpi?.meta?.values?.[0]?.map}
                      styleConfig={kpi?.style}
                      handleStyleConfig={() => {}}
                      isEdit={false}
                      globalConfig={globalConfig}
                    />
                  </>
                </KpiStatus>
              </KpiWrapper>
            </>
          )}
        </KpiCard>
      </KpiContainer>
    </MainContainer>
  );
};

export default ResumeKpiView;
